import { Select } from "@bafe/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { api } from "../../lib/api.js";
import { queryKeys } from "../../lib/query.js";
import { HubCreatableFieldSelect } from "./hub-creatable-field-select.js";
import { compareLocale, defaultColumnVisibility } from "./hub-operation-list-table.js";
import {
	HubOperationBar,
	HubOperationColumnMenu,
	HubOperationEmpty,
	HubOperationFilterClear,
	HubOperationFilterMenu,
	HubOperationFormField,
	HubOperationFormRow,
	HubOperationFormStack,
	HubOperationModal,
	HubOperationListTable,
	HubOperationScreen,
	HubOperationTableEmptyRow,
	HubOperationTablePagination,
	HubOperationSearchField,
	HubOperationSearchToggle,
	HubOperationToolbar,
	hubTableSortIndicator,
	toggleHubSort,
	useHubOperationSearchFocus,
} from "./hub-operation-shell.js";
import { useHubOperationPagination } from "./hub-operation-pagination.js";
import type { Books } from "./hub-operation-types.js";
import {
	customerLabel,
	customersOf,
	projectRow,
	siteRow,
	sitesForCustomer,
	SITE_STATUSES,
	textOf,
	useHubSave,
	workStatusLabel,
	workTypesOf,
} from "./hub-operation-shared.js";
import { WorkTypeBadges } from "./hub-operation-work.js";

export type { Books, CustomerSiteRow, ProjectRow } from "./hub-operation-types.js";

type SiteColumnId = "name" | "customer" | "state" | "status" | "contact";
const SITE_TABLE_COLUMNS: Array<{ id: SiteColumnId; label: string; sortable?: boolean }> = [
	{ id: "name", label: "Name", sortable: true },
	{ id: "customer", label: "Customer", sortable: true },
	{ id: "state", label: "State", sortable: true },
	{ id: "status", label: "Status", sortable: true },
	{ id: "contact", label: "Contact", sortable: true },
];

type ProjectColumnId = "name" | "customer" | "site" | "note";
const PROJECT_TABLE_COLUMNS: Array<{ id: ProjectColumnId; label: string; sortable?: boolean }> = [
	{ id: "name", label: "Project", sortable: true },
	{ id: "customer", label: "Customer", sortable: true },
	{ id: "site", label: "Site", sortable: true },
	{ id: "note", label: "Note", sortable: true },
];

function siteMatchesSearch(books: Books, row: Books["customerSites"][number], query: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	return [
		row.name,
		row.state,
		row.contactPerson,
		row.phone,
		row.address,
		customerLabel(books, row.customerId),
	].some((part) => (part ?? "").toLowerCase().includes(q));
}

function projectMatchesSearch(books: Books, row: Books["projects"][number], query: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	const site = row.siteId ? siteRow(books, row.siteId) : undefined;
	const customer = customerLabel(books, row.customerId ?? site?.customerId ?? "");
	return [row.name, row.note, customer, site?.name].some((part) => (part ?? "").toLowerCase().includes(q));
}

function CustomerSiteFormFields({
	books,
	prefix,
	editing,
}: {
	books: Books;
	prefix: string;
	editing?: Books["customerSites"][number] | null;
}) {
	const customers = customersOf(books);
	const [customerId, setCustomerId] = useState(editing?.customerId ?? "");
	return (
		<HubOperationFormStack key={editing?.id ?? "new"}>
			<HubOperationFormField label="Name" htmlFor={`${prefix}-name`}>
				<input id={`${prefix}-name`} name="name" defaultValue={editing?.name ?? ""} placeholder="Johor branch" required />
			</HubOperationFormField>
			<HubOperationFormField label="Customer" htmlFor={`${prefix}-customer`}>
				<Select
					id={`${prefix}-customer`}
					name="customerId"
					value={customerId}
					onChange={(event) => setCustomerId(event.target.value)}
					required
				>
					<option value="">Choose customer</option>
					{customers.map((row) => (
						<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
					))}
				</Select>
			</HubOperationFormField>
			<HubOperationFormField label="Address" htmlFor={`${prefix}-address`}>
				<input id={`${prefix}-address`} name="address" defaultValue={editing?.address ?? ""} />
			</HubOperationFormField>
			<HubOperationFormRow>
				<HubOperationFormField label="State" htmlFor={`${prefix}-state`}>
					<input id={`${prefix}-state`} name="state" defaultValue={editing?.state ?? ""} />
				</HubOperationFormField>
				<HubOperationFormField label="Status" htmlFor={`${prefix}-status`}>
					<Select id={`${prefix}-status`} name="status" defaultValue={editing?.status ?? "active"}>
						{SITE_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
					</Select>
				</HubOperationFormField>
			</HubOperationFormRow>
			<HubOperationFormField label="Contact person" htmlFor={`${prefix}-contact`}>
				<input id={`${prefix}-contact`} name="contactPerson" defaultValue={editing?.contactPerson ?? ""} />
			</HubOperationFormField>
			<HubOperationFormField label="Phone" htmlFor={`${prefix}-phone`}>
				<input id={`${prefix}-phone`} name="phone" defaultValue={editing?.phone ?? ""} />
			</HubOperationFormField>
			<HubOperationFormField label="Notes" htmlFor={`${prefix}-notes`}>
				<textarea id={`${prefix}-notes`} name="notes" rows={3} defaultValue={editing?.notes ?? ""} placeholder="Optional" />
			</HubOperationFormField>
		</HubOperationFormStack>
	);
}

function siteRecord(fields: FormData) {
	return {
		name: textOf(fields, "name"),
		customerId: textOf(fields, "customerId"),
		address: textOf(fields, "address"),
		state: textOf(fields, "state"),
		contactPerson: textOf(fields, "contactPerson"),
		phone: textOf(fields, "phone"),
		status: textOf(fields, "status") || "active",
		notes: textOf(fields, "notes"),
	};
}

export function CustomerSitesHub({ books, status, onStatus }: { books: Books; status: string; onStatus: (value: string) => void }) {
	const { docId: siteId } = useParams();
	const navigate = useNavigate();
	const client = useQueryClient();
	const searchRef = useRef<HTMLInputElement>(null);
	const rows = books.customerSites;
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [sortKey, setSortKey] = useState<SiteColumnId>("name");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [columnVisibility, setColumnVisibility] = useState(() => defaultColumnVisibility(SITE_TABLE_COLUMNS));
	useHubOperationSearchFocus(searchOpen, searchRef);

	const searchMatched = useMemo(
		() => rows.filter((row) => siteMatchesSearch(books, row, searchQuery)),
		[books, rows, searchQuery],
	);
	const visible = useMemo(() => {
		let list = searchMatched;
		if (status) list = list.filter((row) => row.status === status);
		return list;
	}, [searchMatched, status]);
	const sorted = useMemo(() => {
		const copy = [...visible];
		copy.sort((a, b) => {
			let av = "";
			let bv = "";
			switch (sortKey) {
				case "customer":
					av = customerLabel(books, a.customerId);
					bv = customerLabel(books, b.customerId);
					break;
				case "status":
					av = SITE_STATUSES.find(([v]) => v === a.status)?.[1] ?? a.status ?? "";
					bv = SITE_STATUSES.find(([v]) => v === b.status)?.[1] ?? b.status ?? "";
					break;
				case "contact":
					av = a.contactPerson ?? "";
					bv = b.contactPerson ?? "";
					break;
				case "state":
					av = a.state ?? "";
					bv = b.state ?? "";
					break;
				default:
					av = a.name ?? "";
					bv = b.name ?? "";
			}
			return compareLocale(av, bv, sortDir);
		});
		return copy;
	}, [visible, sortKey, sortDir, books]);

	const sitesPaginationResetKey = `${searchQuery}|${status}|${sortKey}|${sortDir}`;
	const sitesPagination = useHubOperationPagination(sorted.length, sitesPaginationResetKey);
	const pagedSites = useMemo(() => sitesPagination.sliceRows(sorted), [sitesPagination.sliceRows, sorted]);

	const statusCounts = useMemo(() => {
		const counts = new Map<string, number>();
		for (const row of searchMatched) counts.set(row.status ?? "", (counts.get(row.status ?? "") ?? 0) + 1);
		return counts;
	}, [searchMatched]);

	const [editing, setEditing] = useState<(typeof rows)[number] | null>(null);

	const form = useHubSave(async (fields) => {
		const record = siteRecord(fields);
		if (editing?.id) await api.updateHubBook({ list: "customerSites", id: editing.id, record });
		else {
			const created = await api.appendHubBook({ list: "customerSites", record });
			if (created.id) navigate(`/hub/customer-sites/${created.id}`);
		}
		setEditing(null);
	});

	async function remove(id: number) {
		const linked = books.projects.some((row) => row.siteId === String(id));
		if (linked) {
			window.alert("This site still has projects. Move or remove those projects first.");
			return;
		}
		if (!window.confirm("Remove this site?")) return;
		await api.deleteHubBook({ list: "customerSites", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
		if (siteId === String(id)) navigate("/hub/customer-sites");
	}

	const detail = siteId ? siteRow(books, siteId) : undefined;

	if (siteId && detail) {
		const projects = books.projects.filter((row) => row.siteId === siteId);
		const customer = customerLabel(books, detail.customerId);
		return (
			<div>
				<HubOperationBar sub={customer ? `${customer} · ${detail.name}` : detail.name}>
					<Link className="btn ghost" to="/hub/customer-sites">All sites</Link>
					<button className="btn ghost" type="button" onClick={() => { setEditing(detail); form.setOpen(true); }}>Edit</button>
					{detail.id ? (
						<button className="btn ghost" type="button" onClick={() => void remove(detail.id!)}>Delete</button>
					) : null}
				</HubOperationBar>
				<div className="panel grid">
					<div className="grid two">
						<div><span className="quiet">Customer</span><p>{customer || "—"}</p></div>
						<div><span className="quiet">Status</span><p>{SITE_STATUSES.find(([v]) => v === detail.status)?.[1] ?? detail.status ?? "—"}</p></div>
						<div><span className="quiet">Contact</span><p>{detail.contactPerson || "—"}</p></div>
						<div><span className="quiet">Phone</span><p>{detail.phone || "—"}</p></div>
					</div>
					<div><span className="quiet">Address</span><p>{[detail.address, detail.state].filter(Boolean).join(", ") || "—"}</p></div>
					{detail.notes ? <div><span className="quiet">Notes</span><p>{detail.notes}</p></div> : null}
				</div>
				<h2>Projects</h2>
				{projects.length === 0 ? <HubOperationEmpty>No projects on this site yet.</HubOperationEmpty> : (
					<div className="table-wrap">
						<table>
							<thead><tr><th>Project</th><th>Note</th></tr></thead>
							<tbody>
								{projects.map((row) => (
									<tr key={row.id ?? row.name}>
										<td data-label="Project">
											{row.id ? <Link className="row-link" to={`/hub/projects/${row.id}`}>{row.name}</Link> : row.name}
										</td>
										<td data-label="Note">{row.note || "—"}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				<HubOperationModal hubForm open={form.open} title="Edit site" error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
					<CustomerSiteFormFields books={books} prefix="site-detail" editing={editing} />
				</HubOperationModal>
			</div>
		);
	}

	if (siteId && !detail) {
		return (
			<div>
				<HubOperationEmpty>That site was not found.</HubOperationEmpty>
				<Link className="btn ghost" to="/hub/customer-sites">All sites</Link>
			</div>
		);
	}

	const searchActive = searchQuery.trim().length > 0;
	const listFiltered = Boolean(status);
	const filtered = searchActive || listFiltered;
	const visibleColumns = SITE_TABLE_COLUMNS.filter((column) => columnVisibility[column.id]);

	function toggleSort(key: SiteColumnId) {
		const next = toggleHubSort(sortKey, sortDir, key);
		setSortKey(next.sortKey as SiteColumnId);
		setSortDir(next.sortDir);
	}

	return (
		<HubOperationScreen>
			<HubOperationBar sub={`${sorted.length} ${sorted.length === 1 ? "site" : "sites"}${filtered ? " · filtered" : ""}`}>
				<HubOperationToolbar
					searchSlot={(
						<HubOperationSearchField
							id="hub-sites-search"
							ariaLabel="Search sites"
							placeholder="Search sites…"
							searchOpen={searchOpen}
							searchQuery={searchQuery}
							searchRef={searchRef}
							onQueryChange={setSearchQuery}
							onEscape={() => { setSearchQuery(""); setSearchOpen(false); }}
						/>
					)}
					tools={(
						<>
							<HubOperationSearchToggle
								searchOpen={searchOpen}
								searchActive={searchActive}
								ariaLabel="Search sites"
								onToggle={() => {
									if (searchOpen) setSearchQuery("");
									setSearchOpen(!searchOpen);
								}}
							/>
							<HubOperationColumnMenu
								columns={SITE_TABLE_COLUMNS}
								visibility={columnVisibility}
								onToggle={(id, checked) => setColumnVisibility((current) => ({ ...current, [id]: checked }))}
							/>
						</>
					)}
					primaryAction={(
						<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New site</button>
					)}
				/>
			</HubOperationBar>
			<div className="task-filters">
				<HubOperationFilterMenu
					label="Status"
					ariaLabel="Site status"
					value={status}
					onPick={onStatus}
					active={Boolean(status)}
					counted
					items={[
						["", "All", searchMatched.length],
						...SITE_STATUSES.map(([id, text]) => [id, text, statusCounts.get(id) ?? 0] as [string, string, number]),
					]}
				/>
				{listFiltered ? <HubOperationFilterClear onClear={() => onStatus("")} /> : null}
			</div>
			<HubOperationListTable>
			<div className="table-wrap hub-inventory-table-wrap">
				<table className="hub-inventory-table">
					<thead>
						<tr>
							{visibleColumns.map((column) => (
								<th key={column.id}>
									{column.sortable ? (
										<button type="button" className="th-sort plain" onClick={() => toggleSort(column.id)}>
											{column.label}
											{hubTableSortIndicator(sortKey, column.id, sortDir)}
										</button>
									) : column.label}
								</th>
							))}
							<th className="tight"></th>
						</tr>
					</thead>
					<tbody>
						{sorted.length === 0 ? (
							<HubOperationTableEmptyRow colSpan={visibleColumns.length + 1}>
								{filtered || rows.length > 0 ? "No sites match your filters." : "No sites yet."}
							</HubOperationTableEmptyRow>
						) : pagedSites.map((row) => (
								<tr key={row.id ?? row.name}>
									{visibleColumns.map((column) => {
										switch (column.id) {
											case "name":
												return (
													<td key={column.id} data-label={column.label}>
														{row.id ? <Link className="row-link" to={`/hub/customer-sites/${row.id}`}>{row.name}</Link> : row.name}
													</td>
												);
											case "customer":
												return <td key={column.id} data-label={column.label}>{customerLabel(books, row.customerId) || "—"}</td>;
											case "state":
												return <td key={column.id} data-label={column.label}>{row.state || "—"}</td>;
											case "status":
												return <td key={column.id} data-label={column.label}>{SITE_STATUSES.find(([v]) => v === row.status)?.[1] ?? row.status ?? "—"}</td>;
											case "contact":
												return <td key={column.id} data-label={column.label}>{row.contactPerson || "—"}</td>;
											default:
												return <td key={column.id}>—</td>;
										}
									})}
									<td className="tight" onClick={(event) => event.stopPropagation()}>
										{row.id ? (
											<details className="select menu">
												<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
												<div className="select-pop">
													<Link className="select-item" to={`/hub/customer-sites/${row.id}`}>View</Link>
													<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
													<button className="select-item" type="button" onClick={() => void remove(row.id!)}>Delete</button>
												</div>
											</details>
										) : null}
									</td>
								</tr>
						))}
					</tbody>
				</table>
			</div>
			<HubOperationTablePagination
				total={sorted.length}
				page={sitesPagination.page}
				pageSize={sitesPagination.pageSize}
				totalPages={sitesPagination.totalPages}
				onPage={sitesPagination.setPage}
				onPageSize={sitesPagination.setPageSize}
				totalNoun="sites"
			/>
			</HubOperationListTable>
			<HubOperationModal hubForm open={form.open} title={editing ? "Edit site" : "New site"} error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
				<CustomerSiteFormFields books={books} prefix="site" editing={editing} />
			</HubOperationModal>
		</HubOperationScreen>
	);
}

function selectableWorkForProject(books: Books, projectId?: number): Books["work"] {
	const projectKey = projectId != null ? String(projectId) : "";
	return books.work.filter((row) => {
		if (row.id == null) return false;
		const linkedProjectId = String(row.projectId ?? "").trim();
		if (projectKey && linkedProjectId === projectKey) return true;
		return !linkedProjectId;
	});
}

async function linkWorkToProject(
	books: Books,
	workIdRaw: string,
	projectId: string,
) {
	const workId = workIdRaw.trim();
	if (!workId) return;
	const row = books.work.find((item) => String(item.id ?? "") === workId);
	if (!row?.id) return;
	const linkedProjectId = String(row.projectId ?? "").trim();
	if (linkedProjectId === projectId) return;
	if (linkedProjectId) {
		throw new Error("That work is already linked to another project.");
	}
	await api.updateHubBook({
		list: "work",
		id: row.id,
		record: { ...row, projectId },
	});
}

function ProjectFormFields({
	books,
	prefix,
	editing,
}: {
	books: Books;
	prefix: string;
	editing?: Books["projects"][number] | null;
}) {
	const customers = customersOf(books);
	const [customerId, setCustomerId] = useState(editing?.customerId ?? "");
	const [siteId, setSiteId] = useState(editing?.siteId ?? "");
	const siteOptions = useMemo(() => sitesForCustomer(books, customerId), [books, customerId]);
	const workOptions = useMemo(
		() => selectableWorkForProject(books, editing?.id),
		[books, editing?.id],
	);
	const workSelectItems = useMemo(
		() => [
			["", "No work"] as [string, string],
			...workOptions.map((row) => [String(row.id), row.title] as [string, string]),
		],
		[workOptions],
	);
	const linkedWork = editing?.id != null
		? books.work.filter((row) => row.projectId === String(editing.id))
		: [];
	const defaultWorkId = linkedWork.length === 1 ? String(linkedWork[0]?.id ?? "") : "";
	useEffect(() => {
		if (!editing?.siteId) return;
		const site = siteRow(books, editing.siteId);
		if (site?.customerId && site.customerId !== customerId) setCustomerId(site.customerId);
	}, [books, customerId, editing?.siteId]);
	useEffect(() => {
		if (!siteId) return;
		const site = siteRow(books, siteId);
		if (customerId && site?.customerId && site.customerId !== customerId) setSiteId("");
	}, [customerId, siteId, books]);
	return (
		<HubOperationFormStack key={editing?.id ?? "new"}>
			<HubOperationFormField label="Project name" htmlFor={`${prefix}-name`}>
				<input id={`${prefix}-name`} name="name" defaultValue={editing?.name ?? ""} placeholder="Project A" required />
			</HubOperationFormField>
			<HubOperationFormField label="Customer" htmlFor={`${prefix}-customer`}>
				<Select
					id={`${prefix}-customer`}
					name="customerId"
					value={customerId}
					onChange={(event) => setCustomerId(event.target.value)}
					required
				>
					<option value="">Choose customer</option>
					{customers.map((row) => (
						<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
					))}
				</Select>
			</HubOperationFormField>
			<HubOperationFormField label="Site" htmlFor={`${prefix}-site`}>
				<Select
					id={`${prefix}-site`}
					name="siteId"
					value={siteId}
					onChange={(event) => setSiteId(event.target.value)}
					required
					disabled={!customerId}
				>
					<option value="">{customerId ? "Choose site" : "Choose customer first"}</option>
					{siteOptions.map((row) => (
						<option key={row.id ?? row.name} value={String(row.id ?? "")}>{row.name}</option>
					))}
				</Select>
			</HubOperationFormField>
			<HubOperationFormField label="Note" htmlFor={`${prefix}-note`}>
				<textarea id={`${prefix}-note`} name="note" rows={3} defaultValue={editing?.note ?? ""} placeholder="Optional" />
			</HubOperationFormField>
			<HubCreatableFieldSelect
				key={`${prefix}-work-${editing?.id ?? "new"}-${defaultWorkId}`}
				id={`${prefix}-work`}
				name="workId"
				label="Work"
				defaultValue={defaultWorkId}
				items={workSelectItems}
				allowCreate={false}
				menuInModal
			/>
		</HubOperationFormStack>
	);
}

export function ProjectsHub({ books }: { books: Books }) {
	const { docId: projectId } = useParams();
	const client = useQueryClient();
	const searchRef = useRef<HTMLInputElement>(null);
	const [editing, setEditing] = useState<Books["projects"][number] | null>(null);
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [sortKey, setSortKey] = useState<ProjectColumnId>("name");
	const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
	const [columnVisibility, setColumnVisibility] = useState(() => defaultColumnVisibility(PROJECT_TABLE_COLUMNS));
	useHubOperationSearchFocus(searchOpen, searchRef);
	const rows = books.projects;
	const sorted = useMemo(() => {
		const list = rows.filter((row) => projectMatchesSearch(books, row, searchQuery));
		const copy = [...list];
		copy.sort((a, b) => {
			const siteA = a.siteId ? siteRow(books, a.siteId) : undefined;
			const siteB = b.siteId ? siteRow(books, b.siteId) : undefined;
			let av = "";
			let bv = "";
			switch (sortKey) {
				case "customer":
					av = customerLabel(books, a.customerId ?? siteA?.customerId ?? "");
					bv = customerLabel(books, b.customerId ?? siteB?.customerId ?? "");
					break;
				case "site":
					av = siteA?.name ?? "";
					bv = siteB?.name ?? "";
					break;
				default:
					av = String(a[sortKey] ?? "");
					bv = String(b[sortKey] ?? "");
			}
			return compareLocale(av, bv, sortDir);
		});
		return copy;
	}, [books, rows, searchQuery, sortKey, sortDir]);

	const projectsPaginationResetKey = `${searchQuery}|${sortKey}|${sortDir}`;
	const projectsPagination = useHubOperationPagination(sorted.length, projectsPaginationResetKey);
	const pagedProjects = useMemo(() => projectsPagination.sliceRows(sorted), [projectsPagination.sliceRows, sorted]);

	const form = useHubSave(async (fields) => {
		const customerId = textOf(fields, "customerId");
		const siteId = textOf(fields, "siteId");
		const record = {
			name: textOf(fields, "name"),
			note: textOf(fields, "note"),
			customerId,
			siteId,
		};
		const workId = textOf(fields, "workId");
		if (editing?.id) {
			const projectId = String(editing.id);
			await api.updateHubBook({ list: "projects", id: editing.id, record });
			await linkWorkToProject(books, workId, projectId);
		} else {
			const created = await api.appendHubBook({ list: "projects", record });
			await linkWorkToProject(books, workId, String(created.id));
		}
		setEditing(null);
	});

	async function remove(id: number) {
		const linked = books.work.some((row) => row.projectId === String(id));
		if (linked) {
			window.alert("This project still has work linked. Reassign or remove that work first.");
			return;
		}
		if (!window.confirm("Remove this project?")) return;
		await api.deleteHubBook({ list: "projects", id });
		await client.invalidateQueries({ queryKey: queryKeys.hubRecords });
	}

	const detail = projectId ? projectRow(books, projectId) : undefined;

	if (projectId && detail) {
		const site = detail.siteId ? siteRow(books, detail.siteId) : undefined;
		const customer = customerLabel(books, detail.customerId ?? site?.customerId ?? "");
		const workRows = books.work.filter((row) => row.projectId === projectId);
		return (
			<div>
				<HubOperationBar sub={customer ? `${customer}${site ? ` · ${site.name}` : ""}` : detail.name}>
					<Link className="btn ghost" to="/hub/projects">All projects</Link>
					<button className="btn ghost" type="button" onClick={() => { setEditing(detail); form.setOpen(true); }}>Edit</button>
					{detail.id ? (
						<button className="btn ghost" type="button" onClick={() => void remove(detail.id!)}>Delete</button>
					) : null}
				</HubOperationBar>
				<div className="panel grid">
					<div className="grid two">
						<div><span className="quiet">Customer</span><p>{customer || "—"}</p></div>
						<div><span className="quiet">Site</span><p>{site ? (site.id ? <Link className="row-link" to={`/hub/customer-sites/${site.id}`}>{site.name}</Link> : site.name) : "—"}</p></div>
					</div>
					{detail.note ? <div><span className="quiet">Note</span><p>{detail.note}</p></div> : null}
				</div>
				<h2>Work</h2>
				{workRows.length === 0 ? <HubOperationEmpty>No work on this project yet.</HubOperationEmpty> : (
					<div className="table-wrap">
						<table>
							<thead><tr><th>Work</th><th>Type</th><th>Status</th><th>Date</th></tr></thead>
							<tbody>
								{workRows.map((row) => (
									<tr key={row.id ?? row.title}>
										<td data-label="Work">
											{row.id ? <Link className="row-link" to={`/hub/work?work=${row.id}`}>{row.title}</Link> : row.title}
										</td>
										<td data-label="Type"><WorkTypeBadges types={workTypesOf(row)} /></td>
										<td data-label="Status">{workStatusLabel(row.status)}</td>
										<td data-label="Date">{row.due || "—"}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
				<HubOperationModal hubForm open={form.open} title="Edit project" error={form.error} pending={form.pending} onClose={() => { form.setOpen(false); setEditing(null); }} onSubmit={(fields) => void form.save(fields)}>
					<ProjectFormFields books={books} prefix="project-detail" editing={editing} />
				</HubOperationModal>
			</div>
		);
	}

	if (projectId && !detail) {
		return (
			<div>
				<HubOperationEmpty>That project was not found.</HubOperationEmpty>
				<Link className="btn ghost" to="/hub/projects">All projects</Link>
			</div>
		);
	}

	const searchActive = searchQuery.trim().length > 0;
	const visibleColumns = PROJECT_TABLE_COLUMNS.filter((column) => columnVisibility[column.id]);

	function toggleProjectSort(key: ProjectColumnId) {
		const next = toggleHubSort(sortKey, sortDir, key);
		setSortKey(next.sortKey as ProjectColumnId);
		setSortDir(next.sortDir);
	}

	return (
		<HubOperationScreen>
			<HubOperationBar sub={`${sorted.length} ${sorted.length === 1 ? "project" : "projects"}${searchActive ? " · filtered" : ""}`}>
				<HubOperationToolbar
					searchSlot={(
						<HubOperationSearchField
							id="hub-projects-search"
							ariaLabel="Search projects"
							placeholder="Search projects…"
							searchOpen={searchOpen}
							searchQuery={searchQuery}
							searchRef={searchRef}
							onQueryChange={setSearchQuery}
							onEscape={() => { setSearchQuery(""); setSearchOpen(false); }}
						/>
					)}
					tools={(
						<>
							<HubOperationSearchToggle
								searchOpen={searchOpen}
								searchActive={searchActive}
								ariaLabel="Search projects"
								onToggle={() => {
									if (searchOpen) setSearchQuery("");
									setSearchOpen(!searchOpen);
								}}
							/>
							<HubOperationColumnMenu
								columns={PROJECT_TABLE_COLUMNS}
								visibility={columnVisibility}
								onToggle={(id, checked) => setColumnVisibility((current) => ({ ...current, [id]: checked }))}
							/>
						</>
					)}
					primaryAction={(
						<button className="btn" type="button" onClick={() => { setEditing(null); form.setOpen(true); }}>New project</button>
					)}
				/>
			</HubOperationBar>
			<HubOperationListTable>
			<div className="table-wrap hub-inventory-table-wrap">
				<table className="hub-inventory-table">
					<thead>
						<tr>
							{visibleColumns.map((column) => (
								<th key={column.id}>
									{column.sortable ? (
										<button type="button" className="th-sort plain" onClick={() => toggleProjectSort(column.id)}>
											{column.label}
											{hubTableSortIndicator(sortKey, column.id, sortDir)}
										</button>
									) : column.label}
								</th>
							))}
							<th className="tight"></th>
						</tr>
					</thead>
					<tbody>
						{sorted.length === 0 ? (
							<HubOperationTableEmptyRow colSpan={visibleColumns.length + 1}>
								{searchActive || rows.length > 0 ? "No projects match your search." : "No projects yet."}
							</HubOperationTableEmptyRow>
						) : pagedProjects.map((row) => {
								const site = row.siteId ? siteRow(books, row.siteId) : undefined;
								const customer = customerLabel(books, row.customerId ?? site?.customerId ?? "");
								return (
									<tr key={row.id ?? row.name}>
										{visibleColumns.map((column) => {
											switch (column.id) {
												case "name":
													return (
														<td key={column.id} data-label={column.label}>
															{row.id ? <Link className="row-link" to={`/hub/projects/${row.id}`}>{row.name}</Link> : row.name}
														</td>
													);
												case "customer":
													return <td key={column.id} data-label={column.label}>{customer || "—"}</td>;
												case "site":
													return <td key={column.id} data-label={column.label}>{site?.name ?? "—"}</td>;
												case "note":
													return <td key={column.id} data-label={column.label}>{row.note || "—"}</td>;
												default:
													return <td key={column.id}>—</td>;
											}
										})}
										<td className="tight" onClick={(event) => event.stopPropagation()}>
											{row.id ? (
												<details className="select menu">
													<summary aria-label="Actions"><svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="12" cy="19" r="1.8" /></svg></summary>
													<div className="select-pop">
														<Link className="select-item" to={`/hub/projects/${row.id}`}>View</Link>
														<button className="select-item" type="button" onClick={() => { setEditing(row); form.setOpen(true); }}>Edit</button>
														<button className="select-item" type="button" onClick={() => void remove(row.id!)}>Delete</button>
													</div>
												</details>
											) : null}
										</td>
									</tr>
								);
						})}
					</tbody>
				</table>
			</div>
			<HubOperationTablePagination
				total={sorted.length}
				page={projectsPagination.page}
				pageSize={projectsPagination.pageSize}
				totalPages={projectsPagination.totalPages}
				onPage={projectsPagination.setPage}
				onPageSize={projectsPagination.setPageSize}
				totalNoun="projects"
			/>
			</HubOperationListTable>
			<HubOperationModal
				hubForm
				open={form.open}
				title={editing ? "Edit project" : "New project"}
				error={form.error}
				pending={form.pending}
				onClose={() => { form.setOpen(false); setEditing(null); }}
				onSubmit={(fields) => void form.save(fields)}
			>
				<ProjectFormFields books={books} prefix="project" editing={editing} />
			</HubOperationModal>
		</HubOperationScreen>
	);
}

export function CustomerSitesSection({ books, customerId }: { books: Books; customerId: string }) {
	const sites = books.customerSites.filter((row) => row.customerId === customerId);
	if (sites.length === 0) return <p className="quiet">No sites yet.</p>;
	return (
		<ul className="link-list">
			{sites.map((row) => (
				<li key={row.id ?? row.name}>
					{row.id ? <Link className="row-link" to={`/hub/customer-sites/${row.id}`}>{row.name}</Link> : row.name}
					{row.state ? <span className="quiet"> · {row.state}</span> : null}
				</li>
			))}
		</ul>
	);
}

export function WorkProjectContext({ books, projectId }: { books: Books; projectId: string }) {
	const project = projectRow(books, projectId);
	if (!project) return null;
	const site = project.siteId ? siteRow(books, project.siteId) : undefined;
	const customer = customerLabel(books, project.customerId ?? site?.customerId ?? "");
	return (
		<div className="grid two quiet">
			<div><span>Customer</span><p>{customer || "—"}</p></div>
			<div><span>Site</span><p>{site?.name ?? "—"}</p></div>
		</div>
	);
}
